/**
 * Paragraph Transformer - EditorJS Paragraph 자동 변환 모듈
 *
 * 노션과 같이 paragraph에서 특정 패턴을 입력하면 자동으로 변환합니다:
 * - "- " → Unordered List
 * - "1. " → Ordered List
 * - "# " → H1 Header
 * - "## " → H2 Header
 * - "### " → H3 Header
 * - "#### " → H4 Header
 * - "##### " → H5 Header
 * - "###### " → H6 Header
 * - "---" → Delimiter (가로줄)
 * - "| " → Quote (인용)
 * - "[^n]" → Footnote (각주)
 */

import FootnoteTool from '@/editorjs/tools/footnote/index';
import { isStandaloneUrl, normalizeUrl, showUrlInsertMenu } from '@/editorjs/url_insert_menu';

// 변환 패턴 정의
const PATTERNS = {
    unorderedList: /^-\s/,      // "- " (unordered list) - 뒤에 텍스트가 있어도 변환
    orderedList: /^1\.\s/,      // "1. " (ordered list) - 뒤에 텍스트가 있어도 변환
    h1: /^#\s/,                 // "# " (H1) - 뒤에 텍스트가 있어도 변환
    h2: /^##\s/,                // "## " (H2) - 뒤에 텍스트가 있어도 변환
    h3: /^###\s/,               // "### " (H3) - 뒤에 텍스트가 있어도 변환
    h4: /^####\s/,              // "#### " (H4) - 뒤에 텍스트가 있어도 변환
    h5: /^#####\s/,             // "##### " (H5) - 뒤에 텍스트가 있어도 변환
    h6: /^######\s/,            // "###### " (H6) - 뒤에 텍스트가 있어도 변환
    delimiter: /^---$/,         // "---" (delimiter) - 정확히 ---만 입력 시 변환
    quote: /^\|\s/              // "| " (quote) - 뒤에 텍스트가 있어도 변환
};

// 인라인 각주 패턴: [^1], [^2], [^99] 등
const FOOTNOTE_INLINE_PATTERN = /\[\^\d+\]/;

// 변환 중임을 표시하는 플래그 (동시 변환 방지)
let isConverting = false;

/**
 * 현재 에디터가 Column 내부 에디터인지 확인
 * @param {HTMLElement} holderElement - 에디터 holder 요소
 * @returns {boolean}
 */
function isInsideColumn(holderElement) {
    return holderElement?.closest('.ce-columns__col') !== null;
}

/**
 * 메인 설정 함수 - EditorJS 에디터에 paragraph 자동 변환 기능을 추가합니다.
 * @param {EditorJS} editor - EditorJS 인스턴스
 * @param {HTMLElement} holderElement - 에디터 컨테이너 요소
 * @returns {Function} cleanup 함수
 */
export function setupParagraphTransformer(editor, holderElement) {
    // 인스턴스별 IME 입력 상태 추적 (클로저로 캡슐화)
    let isComposing = false;

    // Column 내부 에디터 여부 확인 (undo 조작 생략 결정에 사용)
    const skipUndo = isInsideColumn(holderElement);

    // 이벤트 핸들러 정의
    const handleCompositionStart = () => {
        isComposing = true;
    };

    const handleCompositionEnd = () => {
        isComposing = false;
    };

    const handleInput = (event) => {
        if (isComposing) return; // IME 입력 중에는 무시
        if (!editor || !editor.blocks) return; // Editor 준비 상태 확인
        if (isConverting) return; // 변환 중에는 무시

        // 부모 에디터에서 Column 내부 이벤트는 무시
        if (!skipUndo && event.target.closest('.ce-columns__col')) return;

        // 인라인 각주 패턴 [^n] 체크 (블록 변환보다 먼저)
        if (handleFootnoteInlineInput(event.target)) return;

        handleParagraphInput(editor, event, skipUndo);
    };

    // URL 단독 붙여넣기 → 삽입 방식 선택 팝업
    // capture로 EditorJS보다 먼저 클립보드를 확보하되 preventDefault는 하지 않아
    // 기본 붙여넣기(텍스트/인라인 링크)는 그대로 진행되게 둔다.
    const handlePaste = (event) => {
        if (isComposing) return;
        if (!editor || !editor.blocks) return;

        // 부모 에디터에서 Column 내부 이벤트는 무시 (해당 nested editor가 직접 처리)
        if (!skipUndo && event.target.closest?.('.ce-columns__col')) return;

        const clipboard = event.clipboardData || window.clipboardData;
        if (!clipboard) return;

        const pasted = (clipboard.getData('text') || '').trim();
        if (!isStandaloneUrl(pasted)) return;

        // 붙여넣기 적용 전 paragraph innerHTML 캡처 (capture phase라 아직 DOM 미변경)
        // → URL 제거 후 기존 텍스트 복원용. (wasEmpty는 붙여넣기 후 결과로 재판정)
        const editableBefore = event.target.closest?.('[contenteditable="true"]');
        const originalHtml = editableBefore ? editableBefore.innerHTML : '';

        // EditorJS가 붙여넣기를 삽입할 때까지 기다린 뒤 팝업 표시
        requestAnimationFrame(() => {
            if (!editor || !editor.blocks) return;

            let blockIndex;
            try {
                blockIndex = editor.blocks.getCurrentBlockIndex();
            } catch (e) {
                return;
            }
            const block = blockIndex != null && blockIndex >= 0
                ? editor.blocks.getBlockByIndex(blockIndex)
                : null;
            if (!block || block.name !== 'paragraph') return;

            // 이 paragraph에 URL이 실제로 들어갔는지 확인 (이미 텍스트가 있어도 허용)
            const paraEl = block.holder?.querySelector('.ce-paragraph');
            const pastedHtml = paraEl ? paraEl.innerHTML : '';
            const newText = block.holder?.textContent || '';
            if (!newText.includes(pasted)) return;

            // 빈 줄에 URL만 붙여넣었는지(=줄 전체가 URL) 여부를 결과 기준으로 재판정
            const wasEmpty = newText.trim() === pasted;

            // 붙여넣은 텍스트의 위치 기준으로 팝업 배치
            let anchorRect = null;
            try {
                const selection = window.getSelection();
                if (selection && selection.rangeCount > 0) {
                    const rect = selection.getRangeAt(0).getBoundingClientRect();
                    if (rect && (rect.width || rect.height || rect.left || rect.top)) {
                        anchorRect = rect;
                    }
                }
            } catch (e) { /* 무시 */ }
            if (!anchorRect && block.holder) {
                anchorRect = block.holder.getBoundingClientRect();
            }
            if (!anchorRect) return;

            showUrlInsertMenu({
                editor,
                url: normalizeUrl(pasted),
                blockId: block.id,
                anchorRect,
                skipUndo,
                originalHtml,
                pastedHtml,
                wasEmpty,
            });
        });
    };

    // 이벤트 리스너 등록
    holderElement.addEventListener('compositionstart', handleCompositionStart);
    holderElement.addEventListener('compositionend', handleCompositionEnd);
    holderElement.addEventListener('input', handleInput);
    holderElement.addEventListener('paste', handlePaste, true);

    // Cleanup 함수 반환
    return () => {
        holderElement.removeEventListener('compositionstart', handleCompositionStart);
        holderElement.removeEventListener('compositionend', handleCompositionEnd);
        holderElement.removeEventListener('input', handleInput);
        holderElement.removeEventListener('paste', handlePaste, true);
    };
}

/**
 * 인라인 각주 패턴 [^n] 감지 및 변환
 * 텍스트 노드에서 [^1], [^2] 등의 마크다운 각주 패턴을 찾아 sup[data-footnote]로 변환합니다.
 * @param {HTMLElement} target - input 이벤트 타겟
 * @returns {boolean} 변환 발생 여부
 */
function handleFootnoteInlineInput(target) {
    const editorEl = target.closest('.codex-editor');
    if (!editorEl) return false;

    const contentEl = target.closest('[contenteditable="true"]') || target;
    const walker = document.createTreeWalker(contentEl, NodeFilter.SHOW_TEXT);
    let textNode;

    while (textNode = walker.nextNode()) {
        const match = textNode.textContent.match(FOOTNOTE_INLINE_PATTERN);
        if (!match) continue;

        // [^n] 텍스트를 삭제하고 그 자리에 range를 만든다
        const startOffset = match.index;
        const endOffset = startOffset + match[0].length;

        const range = document.createRange();
        range.setStart(textNode, startOffset);
        range.setEnd(textNode, endOffset);
        range.deleteContents();

        // insertAt이 sup 삽입 + ZWS + 커서 이동 + 이벤트를 모두 처리
        FootnoteTool.insertAt(range);

        return true;
    }

    return false;
}

/**
 * Paragraph 입력 핸들러 - 패턴 매칭 시 변환
 * @param {EditorJS} editor - EditorJS 인스턴스
 * @param {InputEvent} event - input 이벤트
 * @param {boolean} skipUndo - undo 조작 생략 여부 (Column 내부에서 true)
 */
function handleParagraphInput(editor, event, skipUndo = false) {
    const target = event.target;

    // paragraph 블록 내부인지 확인
    if (!target.classList.contains('ce-paragraph')) {
        return;
    }

    const text = target.textContent || '';

    // 패턴 매칭 및 변환
    let matchedPattern = null;
    let conversionType = null;
    let remainingText = text;

    // delimiter는 정확히 "---"만 매칭 (다른 패턴보다 먼저 체크)
    if (PATTERNS.delimiter.test(text)) {
        matchedPattern = '---';
        conversionType = { type: 'delimiter' };
        remainingText = '';
    } else if (PATTERNS.h6.test(text)) {
        matchedPattern = '###### ';
        conversionType = { type: 'header', level: 6 };
        remainingText = text.replace(/^######\s/, '');
    } else if (PATTERNS.h5.test(text)) {
        matchedPattern = '##### ';
        conversionType = { type: 'header', level: 5 };
        remainingText = text.replace(/^#####\s/, '');
    } else if (PATTERNS.h4.test(text)) {
        matchedPattern = '#### ';
        conversionType = { type: 'header', level: 4 };
        remainingText = text.replace(/^####\s/, '');
    } else if (PATTERNS.h3.test(text)) {
        matchedPattern = '### ';
        conversionType = { type: 'header', level: 3 };
        remainingText = text.replace(/^###\s/, '');
    } else if (PATTERNS.h2.test(text)) {
        matchedPattern = '## ';
        conversionType = { type: 'header', level: 2 };
        remainingText = text.replace(/^##\s/, '');
    } else if (PATTERNS.h1.test(text)) {
        matchedPattern = '# ';
        conversionType = { type: 'header', level: 1 };
        remainingText = text.replace(/^#\s/, '');
    } else if (PATTERNS.unorderedList.test(text)) {
        matchedPattern = '- ';
        conversionType = { type: 'list', style: 'unordered' };
        remainingText = text.replace(/^-\s/, '');
    } else if (PATTERNS.orderedList.test(text)) {
        matchedPattern = '1. ';
        conversionType = { type: 'list', style: 'ordered' };
        remainingText = text.replace(/^1\.\s/, '');
    } else if (PATTERNS.quote.test(text)) {
        matchedPattern = '| ';
        conversionType = { type: 'quote' };
        remainingText = text.replace(/^\|\s/, '');
    }

    if (conversionType) {
        if (conversionType.type === 'header') {
            convertToHeader(editor, conversionType.level, skipUndo, remainingText);
        } else if (conversionType.type === 'list') {
            convertToList(editor, conversionType.style, skipUndo, remainingText);
        } else if (conversionType.type === 'delimiter') {
            convertToDelimiter(editor, skipUndo);
        } else if (conversionType.type === 'quote') {
            convertToQuote(editor, skipUndo, remainingText);
        }
    }
}

/**
 * Paragraph를 List로 변환
 * @param {EditorJS} editor - EditorJS 인스턴스
 * @param {string} style - 'ordered' 또는 'unordered'
 * @param {boolean} skipUndo - undo 조작 생략 여부
 * @param {string} remainingText - 패턴 제거 후 남은 텍스트
 */
async function convertToList(editor, style, skipUndo = false, remainingText = '') {
    if (isConverting) return;
    isConverting = true;

    try {
        // Column 내부가 아닐 때만 UnifiedUndo 히스토리 조작
        const undo = skipUndo ? null : editor.undo;
        const originalShouldSaveHistory = undo?.shouldSaveHistory;
        if (undo) {
            undo.shouldSaveHistory = false;
        }

        const currentBlockIndex = editor.blocks.getCurrentBlockIndex();

        // Paragraph를 List로 원자적 변환 (남은 텍스트 포함)
        const currentBlock = editor.blocks.getBlockByIndex(currentBlockIndex);
        await editor.blocks.convert(
            currentBlock.id,
            'list',
            { style: style, items: [{ content: remainingText, items: [] }] }
        );

        // 커서 위치 복원
        await restoreCursorToList(editor, currentBlockIndex);

        // UnifiedUndo 히스토리 재개 및 수동 저장
        if (undo) {
            undo.shouldSaveHistory = originalShouldSaveHistory;
            requestAnimationFrame(() => {
                if (undo.registerChange) {
                    undo.registerChange();
                }
            });
        }

    } catch (error) {
        console.error('[ParagraphTransformer] Paragraph → List 변환 실패:', error);
    } finally {
        isConverting = false;
    }
}

/**
 * Paragraph를 Header로 변환
 * @param {EditorJS} editor - EditorJS 인스턴스
 * @param {number} level - 헤더 레벨 (1-6)
 * @param {boolean} skipUndo - undo 조작 생략 여부
 * @param {string} remainingText - 패턴 제거 후 남은 텍스트
 */
async function convertToHeader(editor, level, skipUndo = false, remainingText = '') {
    if (isConverting) return;
    isConverting = true;

    try {
        // Column 내부가 아닐 때만 UnifiedUndo 히스토리 조작
        const undo = skipUndo ? null : editor.undo;
        const originalShouldSaveHistory = undo?.shouldSaveHistory;
        if (undo) {
            undo.shouldSaveHistory = false;
        }

        const currentBlockIndex = editor.blocks.getCurrentBlockIndex();

        // Paragraph를 Header로 원자적 변환 (남은 텍스트 포함)
        const currentBlock = editor.blocks.getBlockByIndex(currentBlockIndex);
        await editor.blocks.convert(
            currentBlock.id,
            'header',
            { text: remainingText, level: level }
        );

        // 커서 위치 복원
        await restoreCursorToHeader(editor, currentBlockIndex);

        // UnifiedUndo 히스토리 재개 및 수동 저장
        if (undo) {
            undo.shouldSaveHistory = originalShouldSaveHistory;
            requestAnimationFrame(() => {
                if (undo.registerChange) {
                    undo.registerChange();
                }
            });
        }

    } catch (error) {
        console.error('[ParagraphTransformer] Paragraph → Header 변환 실패:', error);
    } finally {
        isConverting = false;
    }
}

/**
 * Paragraph를 Delimiter로 변환
 * Delimiter는 conversionConfig를 지원하지 않으므로 delete + insert 방식 사용
 * @param {EditorJS} editor - EditorJS 인스턴스
 * @param {boolean} skipUndo - undo 조작 생략 여부
 */
async function convertToDelimiter(editor, skipUndo = false) {
    if (isConverting) return;
    isConverting = true;

    try {
        // Column 내부가 아닐 때만 UnifiedUndo 히스토리 조작
        const undo = skipUndo ? null : editor.undo;
        const originalShouldSaveHistory = undo?.shouldSaveHistory;
        if (undo) {
            undo.shouldSaveHistory = false;
        }

        const currentBlockIndex = editor.blocks.getCurrentBlockIndex();

        // 현재 paragraph 삭제
        await editor.blocks.delete(currentBlockIndex);

        // delimiter 삽입
        await editor.blocks.insert('delimiter', {}, undefined, currentBlockIndex, true);

        // delimiter 아래에 새 paragraph 삽입
        await editor.blocks.insert('paragraph', { text: '' }, undefined, currentBlockIndex + 1, true);

        // 새 paragraph로 커서 이동
        await restoreCursorToNewParagraph(editor, currentBlockIndex + 1);

        // UnifiedUndo 히스토리 재개 및 수동 저장
        if (undo) {
            undo.shouldSaveHistory = originalShouldSaveHistory;
            requestAnimationFrame(() => {
                if (undo.registerChange) {
                    undo.registerChange();
                }
            });
        }

    } catch (error) {
        console.error('[ParagraphTransformer] Paragraph → Delimiter 변환 실패:', error);
    } finally {
        isConverting = false;
    }
}

/**
 * Paragraph를 Quote로 변환
 * @param {EditorJS} editor - EditorJS 인스턴스
 * @param {boolean} skipUndo - undo 조작 생략 여부
 * @param {string} remainingText - 패턴 제거 후 남은 텍스트
 */
async function convertToQuote(editor, skipUndo = false, remainingText = '') {
    if (isConverting) return;
    isConverting = true;

    try {
        // Column 내부가 아닐 때만 UnifiedUndo 히스토리 조작
        const undo = skipUndo ? null : editor.undo;
        const originalShouldSaveHistory = undo?.shouldSaveHistory;
        if (undo) {
            undo.shouldSaveHistory = false;
        }

        const currentBlockIndex = editor.blocks.getCurrentBlockIndex();

        // Paragraph를 Quote로 원자적 변환 (남은 텍스트 포함)
        const currentBlock = editor.blocks.getBlockByIndex(currentBlockIndex);
        await editor.blocks.convert(
            currentBlock.id,
            'quote',
            { text: remainingText, caption: '', alignment: 'left' }
        );

        // 커서 위치 복원
        await restoreCursorToQuote(editor, currentBlockIndex);

        // UnifiedUndo 히스토리 재개 및 수동 저장
        if (undo) {
            undo.shouldSaveHistory = originalShouldSaveHistory;
            requestAnimationFrame(() => {
                if (undo.registerChange) {
                    undo.registerChange();
                }
            });
        }

    } catch (error) {
        console.error('[ParagraphTransformer] Paragraph → Quote 변환 실패:', error);
    } finally {
        isConverting = false;
    }
}

/**
 * Quote 블록으로 커서 복원
 * @param {EditorJS} editor - EditorJS 인스턴스
 * @param {number} blockIndex - 블록 인덱스
 */
async function restoreCursorToQuote(editor, blockIndex) {
    return new Promise((resolve) => {
        setTimeout(() => {
            requestAnimationFrame(() => {
                if (!editor || !editor.blocks) {
                    resolve();
                    return;
                }
                const quoteBlock = editor.blocks.getBlockByIndex(blockIndex);
                if (quoteBlock && quoteBlock.holder) {
                    const quoteElement = quoteBlock.holder.querySelector('.cdx-quote__text');
                    if (quoteElement) {
                        const textLength = quoteElement.textContent.length;
                        setCursorToElement(quoteElement, textLength);
                    }
                }
                resolve();
            });
        }, 0);
    });
}

/**
 * 새로 생성된 Paragraph 블록으로 커서 이동
 * @param {EditorJS} editor - EditorJS 인스턴스
 * @param {number} blockIndex - 블록 인덱스
 */
async function restoreCursorToNewParagraph(editor, blockIndex) {
    return new Promise((resolve) => {
        setTimeout(() => {
            requestAnimationFrame(() => {
                if (!editor || !editor.blocks) {
                    resolve();
                    return;
                }
                const paragraphBlock = editor.blocks.getBlockByIndex(blockIndex);
                if (paragraphBlock && paragraphBlock.holder) {
                    const paragraphElement = paragraphBlock.holder.querySelector('.ce-paragraph');
                    if (paragraphElement) {
                        setCursorToElement(paragraphElement, 0);
                    }
                }
                resolve();
            });
        }, 0);
    });
}

/**
 * List 블록으로 커서 복원
 * @param {EditorJS} editor - EditorJS 인스턴스
 * @param {number} blockIndex - 블록 인덱스
 */
async function restoreCursorToList(editor, blockIndex) {
    return new Promise((resolve) => {
        setTimeout(() => {
            requestAnimationFrame(() => {
                if (!editor || !editor.blocks) {
                    resolve();
                    return;
                }
                const listBlock = editor.blocks.getBlockByIndex(blockIndex);
                if (listBlock && listBlock.holder) {
                    const contentElement = listBlock.holder.querySelector('.cdx-list__item-content');
                    if (contentElement) {
                        const textLength = contentElement.textContent.length;
                        setCursorToElement(contentElement, textLength);
                    }
                }
                resolve();
            });
        }, 0);
    });
}

/**
 * Header 블록으로 커서 복원
 * @param {EditorJS} editor - EditorJS 인스턴스
 * @param {number} blockIndex - 블록 인덱스
 */
async function restoreCursorToHeader(editor, blockIndex) {
    return new Promise((resolve) => {
        setTimeout(() => {
            requestAnimationFrame(() => {
                if (!editor || !editor.blocks) {
                    resolve();
                    return;
                }
                const headerBlock = editor.blocks.getBlockByIndex(blockIndex);
                if (headerBlock && headerBlock.holder) {
                    const headerElement = headerBlock.holder.querySelector('.ce-header');
                    if (headerElement) {
                        const textLength = headerElement.textContent.length;
                        setCursorToElement(headerElement, textLength);
                    }
                }
                resolve();
            });
        }, 0);
    });
}

/**
 * 특정 요소의 지정된 offset 위치에 커서를 설정
 * @param {HTMLElement} element - 대상 요소
 * @param {number} offset - 커서 위치
 */
function setCursorToElement(element, offset) {
    if (!element || !document.body.contains(element)) return;

    element.focus();

    const selection = window.getSelection();
    const range = document.createRange();

    // 텍스트 노드 찾기
    const getFirstTextNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            return node;
        }
        if (node.firstChild) {
            return getFirstTextNode(node.firstChild);
        }
        return node;
    };

    const textNode = getFirstTextNode(element);

    if (textNode.nodeType === Node.TEXT_NODE) {
        const actualOffset = Math.min(offset, textNode.textContent.length);
        range.setStart(textNode, actualOffset);
        range.collapse(true);
    } else {
        range.setStart(element, 0);
        range.collapse(true);
    }

    selection.removeAllRanges();
    selection.addRange(range);
}

export default { setupParagraphTransformer };
